import {
  CardRecord,
  ParagraphRecallRecord,
  WritingCloze,
  WritingContent,
  NewCardRecord,
  MyIELTSDatabase,
  defaultDb,
  createCard,
  mergeCard,
} from '../../db/indexedDb';
import { generateSmartClozeBlanks } from '../../services/llm/writing';

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 48);

export const placeholderForIndex = (index: number) => `{{blank_${index}}}`;

export interface ClozeTemplate {
  template: string;
  answers: string[];
}

/**
 * Build a cloze template from a reference text and a list of missing phrases.
 * Only replaces the first occurrence of up to maxBlanks phrases.
 */
export function buildClozeTemplate(reference: string, missingUnits: string[], maxBlanks = 5): ClozeTemplate | null {
  const cleaned = Array.from(
    new Set(
      (missingUnits ?? [])
        .map((unit) => unit.trim())
        .filter((unit) => unit.length >= 3)
    )
  );

  if (!cleaned.length || !reference.trim()) return null;

  let template = reference;
  const answers: string[] = [];

  for (let i = 0; i < cleaned.length && answers.length < maxBlanks; i += 1) {
    const unit = cleaned[i];
    const regex = new RegExp(escapeRegExp(unit), 'i');
    if (regex.test(template)) {
      template = template.replace(regex, placeholderForIndex(answers.length));
      answers.push(unit);
    }
  }

  // 如果没有精确匹配，尝试从原文中提取实义词组作为填空
  if (!answers.length) {
    const words = reference.split(/\s+/).filter(w => w.length >= 4);
    const chunks: string[] = [];
    for (let i = 0; i < words.length - 1 && chunks.length < maxBlanks; i += 2) {
      const chunk = words.slice(i, i + 3).join(' ');
      if (chunk.length >= 8) chunks.push(chunk);
    }
    
    for (const chunk of chunks.slice(0, maxBlanks)) {
      const regex = new RegExp(escapeRegExp(chunk), 'i');
      if (regex.test(template)) {
        template = template.replace(regex, placeholderForIndex(answers.length));
        answers.push(chunk);
      }
    }
  }

  if (!answers.length) return null;
  return { template, answers };
}

// 固定 ID：一源一卡
const buildClozeId = (cardId: string) => `${cardId}-cloze`;

export interface GenerateClozeResult {
  created: boolean;
  updated: boolean;
  cardId?: string;
  reason?: string;
}

/**
 * Generate / upsert a writing light-review (cloze) card based on a paragraph recall record.
 * - 一源一卡：每个源卡片只保留一张 Cloze 卡
 * - 优先使用 LLM 智能挖空（高价值表达），失败则降级本地逻辑
 * - 更新而非新建，避免冗余积累
 */
export async function generateWritingClozeFromRecall(
  sourceCard: CardRecord,
  record: ParagraphRecallRecord,
  db: MyIELTSDatabase = defaultDb
): Promise<GenerateClozeResult> {
  const writingContent = (sourceCard.content ?? {}) as WritingContent;
  const reference = sourceCard.writingMeta?.enOriginal ?? writingContent.answer ?? '';
  const missingUnits = record.localScore?.missingUnits ?? [];
  
  if (!reference.trim() || !missingUnits.length) {
    return { created: false, updated: false, reason: 'no-content-or-missing' };
  }

  const now = Date.now();
  const id = buildClozeId(sourceCard.contentId ?? sourceCard.id);

  // 尝试 LLM 智能挖空，失败则用本地逻辑
  let blueprint: ClozeTemplate | null = null;
  try {
    const llmBlanks = await generateSmartClozeBlanks(reference, missingUnits);
    if (llmBlanks && llmBlanks.length > 0) {
      blueprint = buildClozeTemplate(reference, llmBlanks, 6);
    }
  } catch (err) {
    console.warn('[cloze] LLM smart blanks failed, fallback to local', err);
  }
  
  // 降级：本地逻辑
  if (!blueprint) {
    blueprint = buildClozeTemplate(reference, missingUnits, 5);
  }
  
  if (!blueprint) {
    return { created: false, updated: false, reason: 'no-valid-blanks' };
  }

  const clozeMeta: WritingCloze = {
    template: blueprint.template,
    answers: blueprint.answers,
    sourceId: sourceCard.id,
    reference,
    generatedAt: now,
    occurrence: 1,
  };

  const existing = await db.cards.get(id);
  const occurrence = existing ? (existing.writingMeta?.cloze?.occurrence ?? 1) + 1 : 1;
  clozeMeta.occurrence = occurrence;

  const newCard: NewCardRecord = {
    id,
    type: 'writing',
    mode: 'review',
    isRelearn: false,
    topics: sourceCard.topics ?? [],
    tags: [...(sourceCard.tags ?? []), 'writing-cloze'],
    unitId: sourceCard.unitId,
    sourceId: sourceCard.id,
    contentId: id,
    contentVersion: `cloze-v2-${now}`,
    nextReviewDate: existing?.nextReviewDate ?? now, // 立即可练习
    content: {
      prompt: sourceCard.writingMeta?.cnHint ?? '写作填空',
      reference,
      clozeTemplate: blueprint.template,
      clozeAnswers: blueprint.answers,
      sourceCardId: sourceCard.id,
      generatedAt: now,
    },
    writingMeta: {
      cnHint: sourceCard.writingMeta?.cnHint,
      enOriginal: reference,
      cloze: clozeMeta,
    },
  };

  if (!existing) {
    await createCard(newCard, db);
    return { created: true, updated: false, cardId: id };
  }

  // 更新现有卡片，保留 SRS 状态
  const merged = mergeCard(existing, newCard, now, true);
  merged.writingMeta = { ...(merged.writingMeta ?? {}), cloze: clozeMeta };
  merged.content = { ...(existing.content ?? {}), ...newCard.content };
  merged.updatedAt = now;

  await db.cards.put(merged);
  return { created: false, updated: true, cardId: id };
}

